/*
 * at-email-check.js — shared email typo detection for the trade-in forms.
 *
 * One source of truth for all three C2B forms (multi-item, single-item, bulk).
 * Loaded with `defer` so window.ATEmailCheck exists before any form's
 * DOMContentLoaded init runs.
 *
 * Two jobs:
 *   1. Hard-block genuinely malformed addresses (no @, no TLD, spaces, ...).
 *   2. SUGGEST a correction for valid-but-likely-wrong domains
 *      (e.g. gmai.com -> gmail.com) without ever blocking, so an unusual
 *      but real domain is never locked out. Uses a curated provider list +
 *      Levenshtein distance rather than a hardcoded typo map that can never
 *      be complete (which is how `gmai.com` slipped through, TI-F40CDF7E).
 *
 * The pure core (inspect) is exported for Node unit tests via the UMD tail.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;            // Node (tests)
  }
  if (root && !root.ATEmailCheck) {
    root.ATEmailCheck = api;         // browser (idempotent)
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // Curated common providers, UK-weighted (Audio Trader is audiotrader.co.uk).
  var COMMON_DOMAINS = [
    'gmail.com', 'googlemail.com',
    'yahoo.com', 'yahoo.co.uk', 'ymail.com',
    'hotmail.com', 'hotmail.co.uk',
    'outlook.com', 'live.com', 'live.co.uk', 'msn.com',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com',
    'protonmail.com', 'proton.me',
    'gmx.com',
    'btinternet.com', 'sky.com', 'virginmedia.com', 'talktalk.net'
  ];
  var COMMON_SET = {};
  for (var i = 0; i < COMMON_DOMAINS.length; i++) COMMON_SET[COMMON_DOMAINS[i]] = true;

  // Top-level-domain typos we can correct even for domains we don't know.
  var TLD_TYPOS = {
    con: 'com', cmo: 'com', comm: 'com', vom: 'com', xom: 'com',
    ocm: 'com', ccom: 'com', cim: 'com', co: 'com', om: 'com'
  };

  function levenshtein(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (al === 0) return bl;
    if (bl === 0) return al;
    var prev = new Array(bl + 1), cur = new Array(bl + 1), j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (var k = 1; k <= al; k++) {
      cur[0] = k;
      var ca = a.charCodeAt(k - 1);
      for (j = 1; j <= bl; j++) {
        var cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
        var del = prev[j] + 1, ins = cur[j - 1] + 1, sub = prev[j - 1] + cost;
        cur[j] = del < ins ? (del < sub ? del : sub) : (ins < sub ? ins : sub);
      }
      var tmp = prev; prev = cur; cur = tmp;
    }
    return prev[bl];
  }

  // Given a lowercased domain, return a better domain, or null if it looks fine.
  function suggestDomain(domain) {
    if (COMMON_SET[domain]) return null;               // already a known provider

    var dot = domain.lastIndexOf('.');
    if (dot <= 0) return null;
    var root = domain.slice(0, dot);
    var tld = domain.slice(dot + 1);

    // 1. TLD typo -> if fixing the TLD lands on a known provider, take it.
    if (TLD_TYPOS[tld]) {
      var fixed = root + '.' + TLD_TYPOS[tld];
      if (COMMON_SET[fixed]) return fixed;
    }

    // 2. Nearest known provider by edit distance (catches gmai/gmial/gnail...).
    var best = null, bestDist = Infinity;
    for (var i = 0; i < COMMON_DOMAINS.length; i++) {
      var d = levenshtein(domain, COMMON_DOMAINS[i]);
      if (d < bestDist) { bestDist = d; best = COMMON_DOMAINS[i]; }
    }
    if (best && bestDist > 0 && bestDist <= 2) return best;

    // 3. Fall back to a plain TLD correction even for unknown domains.
    if (TLD_TYPOS[tld]) return root + '.' + TLD_TYPOS[tld];

    return null;
  }

  // Total, pure. { malformed: bool, suggestion: string|null }
  // Empty input is not "malformed" here — required-ness is the caller's job.
  function inspect(value) {
    var email = String(value == null ? '' : value).trim();
    if (!email) return { malformed: false, suggestion: null };
    if (email.length > 254) return { malformed: true, suggestion: null };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return { malformed: true, suggestion: null };

    var at = email.lastIndexOf('@');
    var local = email.slice(0, at);
    var domain = email.slice(at + 1).toLowerCase();
    if (domain.indexOf('..') !== -1) return { malformed: true, suggestion: null };

    var better = suggestDomain(domain);
    if (better && better !== domain) return { malformed: false, suggestion: local + '@' + better };
    return { malformed: false, suggestion: null };
  }

  // ---- Browser wiring (no-ops under Node; only touch the DOM when called) ----

  function domainOf(email) {
    var at = String(email).lastIndexOf('@');
    return at === -1 ? '' : email.slice(at + 1).toLowerCase();
  }

  function track(name, params) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  }

  var STYLE_ID = 'at-email-check-style';
  function ensureStyle() {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.at-email-check{display:flex;flex-wrap:wrap;align-items:center;gap:0.5rem;' +
      'margin-top:0.5rem;font-family:var(--at-font-body);font-size:0.875rem;line-height:1.4}' +
      // attribute selector out-specifies the flex rule above, so hidden truly hides
      '.at-email-check[hidden]{display:none}' +
      '.at-email-check--error{color:hsl(var(--at-accent))}' +
      '.at-email-check--suggest{color:hsl(var(--at-foreground) / 0.75)}' +
      '.at-email-check__fix{font-weight:600;color:hsl(var(--at-foreground))}' +
      // shared button base: pill on the token radius, Figtree, smooth token transition
      '.at-email-check__btn{font:inherit;font-size:0.8125rem;line-height:1;cursor:pointer;' +
      'padding:0.4rem 0.85rem;border:1px solid transparent;border-radius:var(--at-radius-sm);' +
      'background:none;transition:background-color var(--at-duration-fast,.15s) ease,' +
      'color var(--at-duration-fast,.15s) ease,border-color var(--at-duration-fast,.15s) ease}' +
      '.at-email-check__btn:focus-visible{outline:2px solid hsl(var(--at-primary));outline-offset:2px}' +
      // "Yes": outline that hovers to primary green (secondary-action pattern; the
      // form keeps its single green Submit as the one primary action on the surface)
      '.at-email-check__btn--yes{border-color:hsl(var(--at-border));color:hsl(var(--at-foreground))}' +
      '.at-email-check__btn--yes:hover{background:hsl(var(--at-primary));' +
      'border-color:hsl(var(--at-primary));color:#fff}' +
      // "No": quiet ghost
      '.at-email-check__btn--no{color:hsl(var(--at-foreground) / 0.6)}' +
      '.at-email-check__btn--no:hover{background:hsl(var(--at-muted));color:hsl(var(--at-foreground))}';
    document.head.appendChild(s);
  }

  // Build (or reuse) the message element that sits under the input.
  function messageEl(input) {
    var el = input._atMsg;
    if (el) return el;
    ensureStyle();
    el = document.createElement('div');
    el.className = 'at-email-check';
    el.hidden = true;
    el.setAttribute('aria-live', 'polite');
    input.insertAdjacentElement('afterend', el);
    input._atMsg = el;
    return el;
  }

  function hideMsg(input) {
    if (input._atMsg) { input._atMsg.hidden = true; input._atMsg.textContent = ''; input._atMsg._atSuggestion = null; }
    input.style.borderColor = '';
  }

  function showError(input, text) {
    var el = messageEl(input);
    el.className = 'at-email-check at-email-check--error';
    el.textContent = text;
    el.hidden = false;
    input.style.borderColor = 'hsl(var(--at-accent))';
  }

  // Render "Did you mean <fix>? Yes / No". Fires the "suggested" event once per value.
  function showSuggestion(input, suggestion, formType) {
    var el = messageEl(input);

    // Fire the "suggested" event once per distinct value (before any early exit).
    if (input._atSuggestedFor !== input.value.trim()) {
      input._atSuggestedFor = input.value.trim();
      track('trade_in_email_suggested', {
        form_type: formType, from_domain: domainOf(input.value.trim()), to_domain: domainOf(suggestion)
      });
    }

    // Idempotent re-show: if this exact suggestion is already on screen, do NOT
    // rebuild. A blur can re-enter showSuggestion while the user is mid-click on
    // a Yes/No button; rebuilding would replace that button and swallow the click.
    if (el._atSuggestion === suggestion && !el.hidden) return;

    el.className = 'at-email-check at-email-check--suggest';
    el.textContent = '';
    // Whole question in one span so flex gap only spaces the buttons, not the "?".
    var q = document.createElement('span');
    q.appendChild(document.createTextNode('Did you mean '));
    var fix = document.createElement('span');
    fix.className = 'at-email-check__fix';
    fix.textContent = suggestion;
    q.appendChild(fix);
    q.appendChild(document.createTextNode('?'));
    el.appendChild(q);

    var yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'at-email-check__btn at-email-check__btn--yes';
    yes.textContent = 'Use this';
    // Keep focus on the input so the button click does not blur it — a blur
    // here would rebuild this element and swallow the very click we want.
    yes.addEventListener('mousedown', function (e) { e.preventDefault(); });
    yes.addEventListener('click', function () {
      var from = domainOf(input.value.trim()), to = domainOf(suggestion);
      input.value = suggestion;
      input._atDismissed = null;
      hideMsg(input);
      track('trade_in_email_corrected', { form_type: formType, from_domain: from, to_domain: to });
      // let the form's own oninput/live gating react to the corrected value
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    });

    var no = document.createElement('button');
    no.type = 'button';
    no.className = 'at-email-check__btn at-email-check__btn--no';
    no.textContent = 'Keep mine';
    no.addEventListener('mousedown', function (e) { e.preventDefault(); });
    no.addEventListener('click', function () {
      var val = input.value.trim();
      input._atDismissed = val;   // remember: this exact value was deliberately kept
      hideMsg(input);
      track('trade_in_email_dismissed', {
        form_type: formType, from_domain: domainOf(val), to_domain: domainOf(suggestion)
      });
    });

    el.appendChild(yes);
    el.appendChild(no);
    el._atSuggestion = suggestion;
    el.hidden = false;
  }

  // Wire an input for live feedback on blur. Idempotent per element.
  function attach(input, opts) {
    if (!input || input._atAttached) return;
    input._atAttached = true;
    var formType = (opts && opts.formType) || input.getAttribute('data-at-form-type') || 'unknown';
    input.addEventListener('blur', function () {
      var val = input.value.trim();
      if (!val) { hideMsg(input); return; }
      var r = inspect(val);
      if (r.malformed) { showError(input, 'Please enter a valid email address'); return; }
      if (r.suggestion && input._atDismissed !== val) { showSuggestion(input, r.suggestion, formType); return; }
      hideMsg(input);
    });
    input.addEventListener('input', function () {
      // typing past a dismissed value re-opens detection; clear stale UI live
      if (input.value.trim() !== input._atDismissed) hideMsg(input);
    });
  }

  // Authoritative submit check. Returns true if the form may proceed.
  // Blocks malformed addresses and shows an un-acknowledged suggestion.
  function gate(input, opts) {
    if (!input) return true;
    var formType = (opts && opts.formType) || input.getAttribute('data-at-form-type') || 'unknown';
    var val = input.value.trim();
    if (!val) {
      showError(input, 'Please enter your email address');
      input.focus();
      return false;
    }
    var r = inspect(val);
    if (r.malformed) {
      showError(input, 'Please enter a valid email address');
      input.focus();
      track('trade_in_email_blocked', { form_type: formType });
      return false;
    }
    if (r.suggestion && input._atDismissed !== val) {
      if (input._atNudged === val) {
        // Speed bump: they saw the suggestion on the last submit and pressed
        // Submit again unchanged — they're keeping their address. Let it through
        // rather than trap a real but unusual domain.
        input._atDismissed = val;
        hideMsg(input);
        track('trade_in_email_dismissed', {
          form_type: formType, from_domain: domainOf(val), to_domain: domainOf(r.suggestion)
        });
        return true;
      }
      // First submit with an unresolved suggestion: hold once, and make it
      // visible (accent border) so the blocked submit is never a silent dead click.
      input._atNudged = val;
      showSuggestion(input, r.suggestion, formType);
      input.style.borderColor = 'hsl(var(--at-accent))';
      input.focus();
      return false;
    }
    hideMsg(input);
    return true;
  }

  return {
    inspect: inspect,
    suggestDomain: suggestDomain,
    levenshtein: levenshtein,
    attach: attach,
    gate: gate
  };
});
